'use client';

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { forwardRef, HTMLAttributes, useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils/classnameVariants';
import { Color, getBackgroundColor } from '../../lib/utils/colors';
import { textToAnchorId } from '../../lib/utils/textToAchorId';
import { Display } from '../atoms/Display';
import { RowContent, TableListRow, TableType } from '../molecules/TableListRow';

export interface TableListProps extends HTMLAttributes<HTMLDivElement> {
  type: TableType;
  title: string;
  backgroundColor?: Color;
  rows: RowContent[];
  columns: string[];
  expandText: string;
  collapseText: string;
  initialRows?: number;
}

const TableList = forwardRef<HTMLDivElement, TableListProps>(
  (
    { className, title, rows, backgroundColor, type, columns, expandText, collapseText, initialRows = 3, ...props },
    ref,
  ) => {
    const [expand, setExpand] = useState<boolean>(false);
    const [maxHeight, setMaxHeight] = useState<string>('');
    const [visibleRows, setVisibleRows] = useState(rows.slice(0, initialRows));
    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const tableHeaderRef = useRef<HTMLTableSectionElement>(null);

    const hasBackground = !!backgroundColor;

    useEffect(() => {
      if (tableBodyRef.current) {
        if (expand) {
          if (type === 'default' && tableHeaderRef.current) {
            setMaxHeight(`${tableHeaderRef.current.scrollHeight + tableBodyRef.current.scrollHeight + 1}px`);
          } else {
            setMaxHeight(`${tableBodyRef.current.scrollHeight + 1}px`);
          }
        } else {
          const sliceHeight = Array.from(tableBodyRef.current.children)
            .slice(0, type === TableType.default ? initialRows * 2 : initialRows * 3)
            .reduce((totalHeight, child) => totalHeight + (child as HTMLElement).offsetHeight, 0);

          if (type === 'default' && tableHeaderRef.current) {
            setMaxHeight(`${sliceHeight + tableHeaderRef.current.scrollHeight}px`);
          } else {
            setMaxHeight(`${sliceHeight}px`);
          }
        }
      }
    }, [expand, rows, type, initialRows]);

    useEffect(() => {
      setVisibleRows(rows);
    }, [rows]);

    const handleExpandToggle = async () => {
      if (!expand) await new Promise(() => setTimeout(() => setExpand(!expand), 300));
      else setExpand(!expand);
    };

    return (
      <div
        {...props}
        ref={ref}
        className={cn(
          'col-span-4 rounded-xl sm:col-span-8',
          hasBackground ? 'px-4 py-6' : '',
          className,
          hasBackground ? getBackgroundColor(backgroundColor, 50) : '',
        )}
        id={textToAnchorId(title)}
      >
        {title && (
          <Display textSize="3xl" tag="h3" className="pb-4">
            {title}
          </Display>
        )}

        <div style={{ maxHeight }} className="overflow-hidden transition-all duration-300 ease-in-out">
          <table className="w-full table-auto">
            {type === 'default' && (
              <thead ref={tableHeaderRef}>
                <tr>
                  {columns.map((column, index) => (
                    <th key={index} className="border-b py-4 pr-6 text-left text-sm font-medium last:pr-0">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody ref={tableBodyRef}>
              {visibleRows.map((row, index) => (
                <TableListRow key={index} {...row} type={type} columns={columns} />
              ))}
            </tbody>
          </table>
        </div>

        {rows.length > initialRows && (
          <div className="flex w-full justify-center">
            <button type="button" className="flex gap-2 py-4 text-sm font-medium" onClick={handleExpandToggle}>
              {expand ? collapseText : expandText}
              {expand ? (
                <ChevronUpIcon className="m-auto size-5 stroke-2" />
              ) : (
                <ChevronDownIcon className="m-auto size-5 stroke-2" />
              )}
            </button>
          </div>
        )}
      </div>
    );
  },
);

TableList.displayName = 'TableList';

export default TableList;
